import {
	extractOnboardingTypeFromStep,
	OnboardingStatus,
	OnboardingStep,
	OnboardingStepsStatus,
	OnboardingStepStatus,
} from "@domain/onboarding/onboarding";
import { OnboardingService } from "@domain/onboarding/onboarding-service";
import { initOnboardingGetStartedSteps, initOnboardingProgramQuickstartSteps } from "@domain/onboarding/onboarding-utils";
import { UserManager } from "@domain/user/user-manager";
import { isNotNil } from "@utils/assert";
import { now } from "@utils/date-utils";
import { observable } from "micro-observables";

export class OnboardingManager {
	private readonly _onboardingStatus = observable<OnboardingStatus | undefined>(undefined);

	private readonly _onboardingStepsStatus = observable<OnboardingStepsStatus | undefined>(undefined);
	onboardingStepsStatus = this._onboardingStepsStatus.readOnly();

	constructor(
		private readonly onboardingService: OnboardingService,
		readonly userManager: UserManager,
	) {
		userManager.user.subscribe(async () => {
			await this.init();
		});
		this._onboardingStatus.subscribe(status => {
			if (isNotNil(status)) {
				const onboardingGetStartedSteps = initOnboardingGetStartedSteps(status);
				const onboardingProgramQuickstartSteps = initOnboardingProgramQuickstartSteps(status);

				const stepsStatus: OnboardingStepsStatus = {
					...onboardingGetStartedSteps,
					...onboardingProgramQuickstartSteps,
				};

				this._onboardingStepsStatus.set(stepsStatus);
			}
		});
	}

	async init(): Promise<void> {
		const status = await this.onboardingService.getOnboardingsStatus();
		this._onboardingStatus.set(status);
	}

	async passStepOnboarding(step: OnboardingStep, shouldDismissButStayDisplayed = false): Promise<void> {
		const onboardingType = extractOnboardingTypeFromStep(step);

		const markStepAsDismissed = () =>
			this._onboardingStatus.update(status =>
				status
					? {
							...status,
							[onboardingType]: [
								...status[onboardingType],
								{
									step,
									date: now(),
								},
							],
						}
					: undefined,
			);

		const stepStatus = this.onboardingStepsStatus.get()?.[step];

		switch (stepStatus) {
			case OnboardingStepStatus.DISMISSED_BUT_DISPLAYED:
				markStepAsDismissed();
				break;
			case OnboardingStepStatus.AVAILABLE: {
				await this.onboardingService.sendOnboardingStepFinished(onboardingType, step);
				if (shouldDismissButStayDisplayed) {
					this._onboardingStepsStatus.update(stepsStatus =>
						stepsStatus
							? {
									...stepsStatus,
									[step]: OnboardingStepStatus.DISMISSED_BUT_DISPLAYED,
								}
							: undefined,
					);
				} else {
					markStepAsDismissed();
				}
				break;
			}
			case OnboardingStepStatus.PENDING:
			case OnboardingStepStatus.DISMISSED:
			default:
				return;
		}
	}
}
